import { useState, useEffect, useCallback, useMemo } from "react";

/**
 * useSearchAndFilter Hook
 *
 * Manages search and filter state with real-time updates.
 * Integrates with API hooks to provide filtered data.
 */
export default function useSearchAndFilter({
  data = [],
  apiHook,
  initialSearch = "",
  initialFilters = {},
  debounceMs = 300,
}) {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [filters, setFilters] = useState(initialFilters);
  const [isFiltering, setIsFiltering] = useState(false);

  // Build API parameters from search and filters
  const apiParams = useMemo(() => {
    const params = {};

    // Add search parameter
    if (searchTerm.trim()) {
      params.search = searchTerm.trim();
    }

    // Add filter parameters
    Object.entries(filters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        params[key] = values.join(",");
      }
    });

    return params;
  }, [searchTerm, filters]);

  // Use API hook with parameters if provided
  const apiResult = apiHook
    ? apiHook(apiParams)
    : { data: [], loading: false, error: null, items: [] };

  // Client-side filtering for when API hook is not used
  const clientFilteredData = useMemo(() => {
    if (apiHook) return apiResult.items || [];

    let filtered = data || [];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        // Search in common fields
        const searchableFields = [
          item.name,
          item.title,
          item.description,
          item.location?.address,
          item.location?.city,
          item.location?.area,
          item.type,
          item.status,
        ].filter(Boolean);

        return searchableFields.some((field) =>
          String(field).toLowerCase().includes(search)
        );
      });
    }

    // Apply filters
    Object.entries(filters).forEach(([filterType, values]) => {
      if (values && values.length > 0) {
        filtered = filtered.filter((item) => {
          const itemValue = item[filterType];
          return values.includes(itemValue);
        });
      }
    });

    return filtered;
  }, [data, searchTerm, filters, apiHook, apiResult.items]);

  // Handle search changes with debouncing
  const handleSearchChange = useCallback(
    (newSearchTerm) => {
      setSearchTerm(newSearchTerm);
      setIsFiltering(true);

      // Reset filtering state after debounce period
      const timeoutId = setTimeout(() => {
        setIsFiltering(false);
      }, debounceMs);

      return () => clearTimeout(timeoutId);
    },
    [debounceMs]
  );

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setIsFiltering(true);

    // Reset filtering state quickly for filters
    setTimeout(() => setIsFiltering(false), 100);
  }, []);

  // Clear all search and filters
  const clearAll = useCallback(() => {
    setSearchTerm("");
    setFilters({});
    setIsFiltering(false);
  }, []);

  // Clear only search
  const clearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Clear only filters
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Check if any search or filters are active
  const hasActiveSearchOrFilters = useMemo(() => {
    const hasSearch = searchTerm.trim().length > 0;
    const hasFilters = Object.values(filters).some(
      (values) => values && values.length > 0
    );
    return hasSearch || hasFilters;
  }, [searchTerm, filters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).reduce(
      (count, values) => count + (values?.length || 0),
      0
    );
  }, [filters]);

  // Generate filter options from data
  const generateFilterOptions = useCallback((items = []) => {
    const options = {};

    if (!items.length) return options;

    // Generate status options
    const statuses = [
      ...new Set(items.map((item) => item.status).filter(Boolean)),
    ];
    if (statuses.length > 0) {
      options.status = statuses.map((status) => ({
        value: status,
        label: status.charAt(0).toUpperCase() + status.slice(1),
        count: items.filter((item) => item.status === status).length,
      }));
    }

    // Generate type options
    const types = [...new Set(items.map((item) => item.type).filter(Boolean))];
    if (types.length > 0) {
      options.type = types.map((type) => ({
        value: type,
        label: type.charAt(0).toUpperCase() + type.slice(1),
        count: items.filter((item) => item.type === type).length,
      }));
    }

    // Generate priority options
    const priorities = [
      ...new Set(items.map((item) => item.priority).filter(Boolean)),
    ];
    if (priorities.length > 0) {
      options.priority = priorities.map((priority) => ({
        value: priority,
        label: priority.charAt(0).toUpperCase() + priority.slice(1),
        count: items.filter((item) => item.priority === priority).length,
      }));
    }

    // Generate phase options (for projects)
    const phases = [
      ...new Set(items.map((item) => item.planning?.phase).filter(Boolean)),
    ];
    if (phases.length > 0) {
      options.phase = phases.map((phase) => ({
        value: phase,
        label: phase.charAt(0).toUpperCase() + phase.slice(1),
        count: items.filter((item) => item.planning?.phase === phase).length,
      }));
    }

    // Generate location options
    const locations = [
      ...new Set(
        items
          .map((item) => item.location?.city || item.location?.area)
          .filter(Boolean)
      ),
    ];
    if (locations.length > 0) {
      options.location = locations.map((location) => ({
        value: location,
        label: location,
        count: items.filter(
          (item) =>
            item.location?.city === location || item.location?.area === location
        ).length,
      }));
    }

    return options;
  }, []);

  return {
    // State
    searchTerm,
    filters,
    isFiltering: isFiltering || apiResult.loading,

    // Data
    data: apiHook ? apiResult.items : clientFilteredData,
    originalData: data,
    filteredCount: apiHook ? apiResult.items.length : clientFilteredData.length,
    totalCount: apiHook
      ? apiResult.meta?.total || apiResult.items.length
      : (data || []).length,

    // API state (if using API hook)
    loading: apiResult.loading,
    error: apiResult.error,
    createItem: apiResult.createItem,
    updateItem: apiResult.updateItem,
    deleteItem: apiResult.deleteItem,
    refresh: apiResult.refresh,

    // Actions
    handleSearchChange,
    handleFiltersChange,
    clearAll,
    clearSearch,
    clearFilters,

    // Helpers
    hasActiveSearchOrFilters,
    activeFilterCount,
    generateFilterOptions,

    // For component props
    searchAndFilterProps: {
      onSearchChange: handleSearchChange,
      onFiltersChange: handleFiltersChange,
      searchValue: searchTerm,
      filters,
    },
  };
}

/**
 * Utility function to create filter options from predefined lists
 */
export const createFilterOptions = (options) => {
  const result = {};

  Object.entries(options).forEach(([key, values]) => {
    if (Array.isArray(values)) {
      result[key] = values.map((value) => ({
        value,
        label:
          typeof value === "string"
            ? value.charAt(0).toUpperCase() + value.slice(1)
            : String(value),
      }));
    } else if (typeof values === "object") {
      result[key] = Object.entries(values).map(([value, label]) => ({
        value,
        label,
      }));
    }
  });

  return result;
};
